import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './CourseDetail.css';
import CourseVideoCards from '../components/course/CourseVideoCards';
import CourseFeatures from '../components/course/CourseFeatures';

// Shared laptop frame — same mockup image for every course
import LaptopFrame from '../assets/mackbook.png';
// Per-course hero videos — swap these paths to match your actual files in assets/Course_Videos/
import OpenRoadsVideo from '../assets/Course_Videos/WaterGEMS.mp4';
import WaterGemsVideo from '../assets/Course_Videos/WaterGEMS.mp4';
import StaadProVideo from '../assets/Course_Videos/WaterGEMS.mp4';
import MicroStationVideo from '../assets/Course_Videos/WaterGEMS.mp4';
import SewerGemsVideo from '../assets/Course_Videos/WaterGEMS.mp4';

const CourseDetail = () => {
  const { courseId } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [courseId]);

  const coursesData = {
    'openroads-designer': {
      title: 'OpenRoads Designer',
      category: 'Engineering Software',
      description: 'Civil infrastructure design software for roadways, offering 3D modeling, terrain modeling, drainage, and corridor modeling in a single platform. This comprehensive solution enables engineers to design, analyze, and document transportation projects with unprecedented efficiency and accuracy.',
      detailedDescription: 'OpenRoads Designer represents the next generation of civil design software, integrating traditional CAD workflows with advanced BIM capabilities. It provides a complete environment for road and highway design, from initial planning through construction documentation, ensuring data continuity throughout the project lifecycle.',
      image: '/images/road.png',
      heroImage: '/images/openroads.jpg',
      heroVideo: OpenRoadsVideo,
      heroContent: {
        badge: 'Bentley Authorized Training Partner',
        subtitle: 'Advanced Civil Engineering Design',
        description: 'Boost productivity by up to 95% with civil infrastructure design features that help you create, modify, and document roadway projects with intelligent automation.'
      },
      duration: '1 Months',
      level: 'Advanced',
      audience: '(infrastructure/Highway),<br />Civil Engineers (CIV)',
      curriculum: [
        'User Interface & Project Setup',
        'Terrain Modeling & Survey Data',
        'Horizontal and Vertical Alignment Design',
        'Corridor Modeling and Templates',
        'Cross-Sections and Profile Views',
        'Earthwork Calculations',
        'Annotation and Sheet Production'
      ],
      whatYoullLearn: [
        'Master survey data import techniques and learn to create accurate terrain models using various data sources including GPS, LiDAR, and total station measurements',
        'Develop skills in generating detailed cross-sections and longitudinal profiles for highway design with proper earthwork calculations',
        'Gain expertise in creating horizontal and vertical alignments with comprehensive corridor modeling for complex road designs',
        'Learn integrated drainage design including culvert analysis, stormwater management, and utility coordination within transportation projects'
      ],
      careerBenefits: [
        'Enhanced employability in the infrastructure sector with skills demanded by DOTs, consulting firms, and construction companies',
        'Skills directly applicable to transportation agencies including highway departments, metro rail authorities, and airport authorities',
        'Competitive advantage in the civil engineering market with proficiency in industry-standard software',
        'Comprehensive preparation for certification exams and professional engineering licensure requirements'
      ],
      provider: 'BENTLEY',
      prerequisites: [
        'Basic computer skills',
        'No prior CAD experience required'
      ]
    },
    'watergems': {
      title: 'OpenFlows WaterGEMS',
      category: 'Engineering Software',
      description: 'Water distribution modeling software that helps engineers plan, design, and optimize water systems with advanced hydraulic modeling capabilities. This powerful tool enables comprehensive analysis of water networks, ensuring reliable and efficient water distribution systems.',
      detailedDescription: 'WaterGEMS provides engineers with sophisticated tools for water distribution system analysis and design. It combines hydraulic modeling capabilities with GIS integration, allowing for comprehensive water system management from source to tap, including pressure management, water quality analysis, and system optimization.',
      image: '/images/pipe.jpg',
      heroImage: '/images/watergems.jpg',
      heroVideo: WaterGemsVideo,
      heroContent: {
        badge: 'Bentley Authorized Training Partner',
        subtitle: 'Intelligent Water Distribution Modeling',
        description: 'Boost productivity by up to 95% with water distribution modeling features that help you plan, design, and optimize water systems with advanced hydraulic analysis.'
      },
      duration: '1 Months',
      level: 'Advanced',
      audience: 'Water Engineers, Civil Engineers (CIV/ENV)',
      curriculum: [
        'Introduction to Hydraulic Modeling',
        'Network Building and Layout',
        'Junctions, Pipes, Pumps, and Tanks',
        'Demand Allocation and Scenarios',
        'Hydraulic Analysis (Steady and EPS)',
        'Fire Flow and Criticality Analysis',
        'Model Calibration and Optimization'
      ],
      whatYoullLearn: [
        'Create comprehensive water network models from scratch including pipe networks, junctions, pumps, tanks, and control valves',
        'Master extended period simulation (EPS) for analyzing system behavior over time with demand patterns and operational scenarios',
        'Develop expertise in scenario comparison to evaluate different design alternatives and operational strategies effectively',
        'Learn demand analysis techniques for residential, commercial, and industrial areas with proper pipe sizing methodologies',
        'Gain skills in fire flow analysis and pump performance evaluation for emergency water supply and system reliability'
      ],
      careerBenefits: [
        'Develop specialized expertise in water system design making you valuable to municipal water utilities and engineering consultants',
        'Acquire skills essential for municipal water departments including system planning, capital improvement projects, and operations',
        'Open consulting opportunities in the water sector with expertise in hydraulic modeling and system optimization',
        'Comprehensive preparation for water industry certifications and professional development in water resources engineering'
      ],
      provider: 'BENTLEY',
      prerequisites: [
        'Civil engineering background',
        'Understanding of hydraulic principles'
      ]
    },
    'staad-pro': {
      title: 'STAAD Pro',
      category: 'Structural Analysis',
      description: 'Comprehensive structural analysis and design software for buildings, bridges, towers, and other structures with advanced modeling capabilities. This industry-standard tool enables engineers to perform complex structural analyses and optimize designs for safety and efficiency.',
      detailedDescription: 'STAAD Pro is the world\'s most popular structural analysis and design software, offering a complete solution for structural engineering. It supports multiple international design codes and provides advanced analysis capabilities including dynamic analysis, finite element analysis, and optimization algorithms for various structure types.',
      image: '/images/staadpro.jpg',
      heroImage: '/images/stadprooo.jpg',
      heroVideo: StaadProVideo,
      heroContent: {
        badge: 'Bentley Authorized Training Partner',
        subtitle: 'Comprehensive Structural Analysis',
        description: 'Boost productivity by up to 95% with structural analysis features that help you analyze and design complex structures with advanced finite element tools.'
      },
      duration: '1 Months',
      level: 'Advanced',
      audience: 'Structural Engineers,<br />Civil Engineers (CIV)',
      curriculum: [
        'Introduction to STAAD Pro',
        'Model Creation and Geometry',
        'Load Definition and Analysis',
        'Steel Design and Optimization',
        'Concrete Design',
        'Foundation Design',
        'Advanced Analysis Techniques'
      ],
      whatYoullLearn: [
        'Master structural modeling techniques for creating accurate 3D models of buildings, bridges, towers, and industrial structures',
        'Develop comprehensive skills in steel design including connection design, member optimization, and code compliance checking',
        'Learn advanced load combinations and analysis methods including static, dynamic, wind, seismic, and thermal analysis',
        'Gain expertise in foundation design principles including shallow foundations, deep foundations, and soil-structure interaction'
      ],
      careerBenefits: [
        'Acquire essential skills for structural engineering roles in design firms, construction companies, and government agencies',
        'Comprehensive preparation for structural design positions including senior engineer, project manager, and technical specialist roles',
        'Gain competitive edge in the construction industry with proficiency in industry-standard structural analysis software',
        'Build strong foundation for advanced structural analysis careers including forensic engineering and specialized consulting'
      ],
      provider: 'BENTLEY',
      prerequisites: [
        'Structural engineering background',
        'Basic understanding of structural analysis'
      ]
    },
    'microstation': {
      title: 'MicroStation for Professionals',
      category: 'CAD Software',
      description: 'Professional CAD software for 2D and 3D design, drafting, and modeling with advanced visualization and documentation capabilities. This powerful platform serves as the foundation for infrastructure design projects across various engineering disciplines.',
      detailedDescription: 'MicroStation is a comprehensive CAD platform that provides precision design and documentation capabilities for infrastructure projects. It offers advanced 3D modeling, visualization, and collaboration tools, making it the preferred choice for large-scale engineering projects requiring high accuracy and interoperability.',
      image: '/images/micro.jpg',
      heroImage: '/images/microstation.jpg',
      heroVideo: MicroStationVideo,
      heroContent: {
        badge: 'Bentley Authorized Training Partner',
        subtitle: 'Professional CAD Design Platform',
        description: 'Boost productivity by up to 95% with professional CAD design features that help you create precise 2D and 3D designs with advanced visualization and collaboration.'
      },
      duration: '1 Months',
      level: 'Advanced',
      audience: 'CAD Professionals, Engineers (ALL)',
      curriculum: [
        'MicroStation Interface and Navigation',
        '2D Drafting and Drawing Tools',
        '3D Modeling Fundamentals',
        'Advanced 3D Techniques',
        'Visualization and Rendering',
        'Documentation and Annotation',
        'File Management and Collaboration'
      ],
      whatYoullLearn: [
        'Master professional CAD drafting techniques with precision drawing tools, dimensioning standards, and technical documentation',
        'Develop advanced 3D modeling skills including solid modeling, surface modeling, and parametric design for complex projects',
        'Learn comprehensive technical documentation practices including drawing standards, annotation, and construction document preparation',
        'Gain expertise in project collaboration using reference files, worksharing, and integrated project delivery workflows'
      ],
      careerBenefits: [
        'Acquire essential CAD skills required across all engineering disciplines including civil, mechanical, electrical, and architectural fields',
        'Develop versatility for multiple industries including infrastructure, manufacturing, plant design, and architectural visualization',
        'Build strong foundation for BIM workflows and digital transformation initiatives in modern engineering practices',
        'Gain competitive advantage in design roles with proficiency in industry-leading CAD platform and advanced visualization skills'
      ],
      provider: 'BENTLEY',
      prerequisites: [
        'Basic computer skills',
        'Understanding of technical drawings'
      ]
    },
    'sewergems': {
      title: 'OpenFlows SewerGEMS',
      category: 'Hydraulic Modeling',
      description: 'Comprehensive sewer system modeling software for planning, design, and analysis of wastewater collection systems. This specialized tool enables engineers to optimize sewer networks for efficiency, reliability, and environmental compliance.',
      detailedDescription: 'SewerGEMS provides advanced hydraulic modeling capabilities specifically designed for wastewater and stormwater systems. It combines sophisticated analysis tools with GIS integration, allowing engineers to design, analyze, and optimize complex sewer networks while ensuring regulatory compliance and system reliability.',
      image: '/images/save.jpg',
      heroImage: '/images/save.jpg',
      heroVideo: SewerGemsVideo,
      heroContent: {
        badge: 'Bentley Authorized Training Partner',
        subtitle: 'Advanced Wastewater System Modeling',
        description: 'Boost productivity by up to 95% with sewer system modeling features that help you design efficient networks with specialized hydraulic analysis.'
      },
      duration: '1 Months',
      level: 'Advanced',
      audience: 'Environmental Engineers, Civil Engineers (CIV/ENV)',
      curriculum: [
        'Introduction to Sewer System Modeling',
        'Network Building and Layout',
        'Flow Estimation and Allocation',
        'Hydraulic Analysis and Design',
        'Pump Station Design',
        'System Optimization',
        'Model Calibration and Validation'
      ],
      whatYoullLearn: [
        'Master comprehensive sewer system modeling including gravity flow networks, pressurized systems, and combined sewer overflows',
        'Develop advanced hydraulic analysis techniques for steady-state and extended period simulations of wastewater collection systems',
        'Learn pump station design including wet well sizing, pump selection, force main analysis, and operational control strategies',
        'Gain expertise in system optimization methods for capacity improvements, cost reduction, and performance enhancement'
      ],
      careerBenefits: [
        'Develop specialized expertise in wastewater management making you valuable to environmental agencies and municipal utilities',
        'Acquire skills for environmental consulting firms specializing in water resources, environmental impact assessment, and sustainability',
        'Create opportunities in municipal planning departments for infrastructure planning, capital improvement programs, and system upgrades',
        'Comprehensive preparation for environmental certifications and professional development in water resources and environmental engineering'
      ],
      provider: 'BENTLEY',
      prerequisites: [
        'Environmental engineering background',
        'Understanding of hydraulic principles'
      ]
    }
  };

  const course = coursesData[courseId] || coursesData['watergems'];

  return (
    <div className="cd-root">

      {/* ══════════════════════════════
          HERO — SPLIT LAYOUT (Fusion style, data-driven)
      ══════════════════════════════ */}
      <section className="acad-hero">

        {/* LEFT — dark panel with text */}
        <div className="acad-hero-left">
          <div className="acad-hero-left-content">
            <span className="acad-badge">
              <span className="acad-badge-dot"></span>
              {course.heroContent.badge}
            </span>

            <h1 className="acad-title">{course.title}</h1>

            <p className="acad-desc">{course.heroContent.description}</p>

            <div className="acad-cta-row">
              <a
                href="https://register.medinitechnologies.in/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="acad-enroll-btn">
                  Enroll Now
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </a>
            </div>
          </div>
        </div>

        {/* RIGHT — dark panel with laptop illustration */}
        <div className="acad-hero-right">
          <div className="acad-laptop-wrapper">
            <div className="acad-screen">
              <video
                key={course.heroVideo}
                src={course.heroVideo}
                autoPlay
                loop
                muted
                playsInline
                className="acad-video"
              />
            </div>

            <img
              src={LaptopFrame}
              alt="Laptop"
              className="acad-laptop-frame"
            />
          </div>

          {/* Floating pills */}
          <div className="acad-pill acad-pill-1">CAD + CAM</div>
          <div className="acad-pill acad-pill-2">Cloud Collaboration</div>
          <div className="acad-pill acad-pill-3">Generative Design</div>
        </div>
      </section>

      {/* ══════════════════════════════
          NEW COURSE COMPONENTS — Video Cards & Features
      ══════════════════════════════ */}
      <CourseVideoCards courseId={courseId} />
      <CourseFeatures courseId={courseId} />

  {/* =========================================
      INTERNAL CSS - HERO STYLES
  ========================================= */}
  <style>{`

    @import url(
      'https://fonts.googleapis.com/css2?family=Inter:wght@400&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap'
    );


    /* =========================================
       ROOT
    ========================================= */

    .cd-root {

      font-family: 'Plus Jakarta Sans', sans-serif;

      margin-top: 70px;

    }


    /* =========================================
       HERO
    ========================================= */

    .acad-hero {

      display: grid;

      grid-template-columns: 1fr 1.1fr;

      min-height: calc(100vh - 70px);

    }


    /* =========================================
       HERO LEFT
    ========================================= */

    .acad-hero-left {

      background: #0c0e14;

      position: relative;

      overflow: hidden;

      display: flex;

      align-items: center;

    }


    /* =========================================
       HERO LEFT CONTENT
    ========================================= */

    .acad-hero-left-content {

      position: relative;

      z-index: 2;

      padding: 72px 56px;

      display: flex;

      flex-direction: column;

      gap: 24px;

      animation: acadFadeInLeft 0.75s ease both;

      text-align: left;

    }


    /* =========================================
       BADGE
    ========================================= */

    .acad-badge {

      display: inline-flex;

      align-items: center;

      gap: 8px;

      padding: 7px 16px;

      border-radius: 999px;

      border: 1px solid rgba(255,255,255,0.18);

      background: rgba(255,255,255,0.05);

      color: rgba(255,255,255,0.65);

      font-family: 'Plus Jakarta Sans', sans-serif;

      font-size: 12.5px;

      font-weight: 500;

      letter-spacing: 0.03em;

      width: fit-content;

    }


    .acad-badge-dot {

      width: 7px;

      height: 7px;

      border-radius: 50%;

      background: #60efaa;

      box-shadow: 0 0 6px #60efaa;

      flex-shrink: 0;

      animation: acadBlink 2s ease-in-out infinite;

    }


    /* =========================================
       TITLE
    ========================================= */

    .acad-title {

      font-family: 'Inter', sans-serif;

      font-style: normal;

      font-size: 36px;

      font-weight: 400;

      line-height: 40px;

      color: rgb(255, 255, 255);

      margin: 0;

      letter-spacing: normal;

    }


    /* =========================================
       DESCRIPTION
    ========================================= */

    .acad-desc {

      font-family: 'Inter', sans-serif;

      font-style: normal;

      font-size: 16px;

      font-weight: 400;

      line-height: 24px;

      color: rgba(255, 255, 255, 0.9);

      margin: 0;

      max-width: 500px;

    }


    /* =========================================
       CTA
    ========================================= */

    .acad-cta-row {

      display: flex;

      align-items: center;

      gap: 16px;

      flex-wrap: wrap;

    }


    .acad-enroll-btn {

      display: inline-flex;

      align-items: center;

      gap: 10px;

      background: #ffffff;

      color: #0c0e14;

      font-family: 'Plus Jakarta Sans', sans-serif;

      font-weight: 700;

      font-size: 15px;

      border: none;

      padding: 14px 30px;

      border-radius: 10px;

      cursor: pointer;

      width: fit-content;

      letter-spacing: 0.01em;

      transition: all 0.3s ease;

      box-shadow: 0 4px 20px rgba(255,255,255,0.12);

    }


    .acad-enroll-btn:hover {

      background: #efefef;

      transform: translateY(-2px);

      box-shadow: 0 8px 28px rgba(255,255,255,0.2);

    }


    .acad-enroll-btn svg {

      transition: transform 0.3s ease;

    }


    .acad-enroll-btn:hover svg {

      transform: translateX(4px);

    }


    /* =========================================
       HERO RIGHT
    ========================================= */

    .acad-hero-right {

      background: #0c0e14;

      position: relative;

      display: flex;

      align-items: center;

      justify-content: center;

      padding: 80px 40px;

      overflow: hidden;

    }


    /* =========================================
       LAPTOP WRAPPER
    ========================================= */

    .acad-laptop-wrapper {

      position: relative;

      width: min(900px, 95%);

      transform: scale(1.2);

    }


    /* =========================================
       LAPTOP FRAME
    ========================================= */

    .acad-laptop-frame {

      width: 100%;

      height: auto;

      display: block;

      position: relative;

      z-index: 2;

    }


    /* =========================================
       VIDEO
    ========================================= */

    .acad-video {

      position: absolute;

      top: 0;

      left: 0;

      width: 100%;

      height: 100%;

      object-fit: cover;

    }


    /* =========================================
       VIDEO SCREEN
    ========================================= */

    .acad-screen {

      position: absolute;

      top: 15%;

      left: 15%;

      width: 70%;

      height: 70%;

      overflow: hidden;

      border-radius: 8px;

      z-index: 1;

    }


    /* =========================================
       FLOATING PILLS
    ========================================= */

    .acad-pill {

      position: absolute;

      background: #1e1e1e;

      border: 1px solid rgba(255,255,255,0.12);

      border-radius: 999px;

      padding: 7px 16px;

      font-family: 'Plus Jakarta Sans', sans-serif;

      font-size: 12px;

      font-weight: 600;

      color: rgba(255,255,255,0.75);

      box-shadow: 0 4px 16px rgba(0,0,0,0.4);

      z-index: 3;

      animation: acadFloatPill 4s ease-in-out infinite alternate;

    }


    .acad-pill-1 {

      top: 15%;

      left: 6%;

      animation-delay: 0s;

    }


    .acad-pill-2 {

      top: 10%;

      right: 8%;

      animation-delay: 0.8s;

    }


    .acad-pill-3 {

      bottom: 15%;

      right: 6%;

      animation-delay: 1.6s;

    }


    /* =========================================
       ANIMATIONS
    ========================================= */

    @keyframes acadFadeInLeft {

      from {

        opacity: 0;

        transform: translateX(-32px);

      }

      to {

        opacity: 1;

        transform: translateX(0);

      }

    }


    @keyframes acadFadeInRight {

      from {

        opacity: 0;

        transform: translateX(32px);

      }

      to {

        opacity: 1;

        transform: translateX(0);

      }

    }


    @keyframes acadBlink {

      0%, 100% {

        opacity: 1;

      }

      50% {

        opacity: 0.25;

      }

    }


    @keyframes acadFloatPill {

      from {

        transform: translateY(0px);

      }

      to {

        transform: translateY(-10px);

      }

    }


    /* =========================================
       TABLET
    ========================================= */

    @media (max-width: 1200px) {

      .acad-pill {

        display: none;

      }

      .acad-laptop-wrapper {

        width: 600px;

      }

    }


    /* =========================================
       MOBILE / TABLET
    ========================================= */

    @media (max-width: 960px) {

      .acad-hero {

        grid-template-columns: 1fr;

      }

      .acad-hero-right {

        padding: 48px 32px;

        min-height: 340px;

      }

      .acad-hero-left-content {

        padding: 52px 32px;

      }

      .acad-laptop-wrapper {

        width: 500px;

        transform: none !important;

      }

    }


    /* =========================================
       MOBILE
    ========================================= */

    @media (max-width: 600px) {

      .acad-hero-left-content {

        padding: 36px 20px;

        gap: 18px;

      }

      .acad-hero-right {

        padding: 32px 16px;

      }

      .acad-cta-row {

        flex-direction: column;

        align-items: flex-start;

      }

      .acad-enroll-btn {

        width: 100%;

        justify-content: center;

      }

      .acad-laptop-wrapper {

        width: 100%;

      }

      .acad-video {

        clip-path: inset(6% 11% 27% 11%);

      }

    }

  `}</style>
</div>

  );
};

export default CourseDetail;
